import { Controller } from "@hotwired/stimulus"

// Cache so that we reuse options when the same template is picked again
const classroomSessionReassignCache = window.classroomSessionReassignCache || (window.classroomSessionReassignCache = new Map());

export default class extends Controller {
  static targets = [
    "templateSelect", 
    "templateSelectContainer", 
    "templateDisplay", 
    "templateDisplayContainer", 
    "sessionSelect"
  ]

  static values = {
    sessionId: Number,
    attendanceId: Number,
    attendanceType: String,
    initialTemplateId: String,
    preloadedOptions: Array,
    preloadedOldOptions: Array,
    moveSessionId: String
  }

  connect() {
    this.loadAvailableSessions();
  }

  showTemplateSelect(event) {
    event.preventDefault();
    if (this.hasTemplateDisplayContainerTarget) {
      this.templateDisplayContainerTarget.classList.add("u-hidden");
    }
    if (this.hasTemplateSelectContainerTarget) {
      this.templateSelectContainerTarget.classList.remove("u-hidden");
    }
    if (this.hasTemplateSelectTarget) {
      this.templateSelectTarget.focus();
    }
  }

  templateChanged() {
    if (this.hasTemplateDisplayTarget) {
      const selectedOption = this.templateSelectTarget.selectedOptions[0];
      if (selectedOption) {
        this.templateDisplayTarget.textContent = selectedOption.textContent;
      }
    }
    if (this.hasTemplateSelectContainerTarget) {
      this.templateSelectContainerTarget.classList.add("u-hidden");
    }
    if (this.hasTemplateDisplayContainerTarget) {
      this.templateDisplayContainerTarget.classList.remove("u-hidden");
    }

    this.loadAvailableSessions();
  }

  loadAvailableSessions() {
    if (!this.hasTemplateSelectTarget || !this.hasSessionSelectTarget) {
      return;
    }

    const templateId = this.templateSelectTarget.value;
    this.resetSessionOptions();

    if (!templateId) {
      return;
    }

    // Pupils with attendance type of both shouldn't be allowed
    // Just return no available sessions for these freaks of nature for now
    if (this.attendanceTypeValue === "both") {
      this.applyOptions({
        available_sessions: [],
        previous_sessions: []
      });
      return;
    }

    // On initial load we can preload the reassign dropdown options 
    // We always know the selected template and each attendee's attendance type
    if ((this.preloadedOptionsValue.length > 0 || this.preloadedOldOptionsValue.length > 0) && templateId === this.initialTemplateIdValue) {
      this.applyOptions({
        available_sessions: this.preloadedOptionsValue || [],
        previous_sessions: this.preloadedOldOptionsValue || []
      });
      return;
    }

    const cacheKey = `${this.sessionIdValue}|${this.attendanceTypeValue}|${templateId}`;
    if (classroomSessionReassignCache.has(cacheKey)) {
      this.applyOptions(classroomSessionReassignCache.get(cacheKey));
      return
    }

    this.sessionSelectTarget.disabled = true;

    fetch(`/schools/classroom/sessions/attendance/${this.attendanceIdValue}/available-reassign-sessions?template_id=${templateId}&attendance_type=${this.attendanceTypeValue}`, {
      headers: { "Accept": "application/json" }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load reassign options");
        }
        return response.json();
      })
      .then((data) => {
        classroomSessionReassignCache.set(cacheKey, data);
        this.applyOptions(data);
      })
      .catch(() => {
        this.showNoSessions("Unable to load sessions");
      })
      .finally(() => {
        this.sessionSelectTarget.disabled = false;
      })
  }

  resetSessionOptions() {
    this.sessionSelectTarget.innerHTML = "";

    const prompt = document.createElement("option");
    prompt.value = "";
    prompt.textContent = "Select a new session";
    this.sessionSelectTarget.appendChild(prompt);
  }

  applyOptions(data) {
    this.resetSessionOptions();

    const availableSessions = data.available_sessions || [];
    const previousSessions = data.previous_sessions || [];

    if (availableSessions.length === 0 && previousSessions.length === 0) {
      this.showNoSessions("No make-up sessions available");
      return;
    }

    if (availableSessions.length > 0) {
      this.appendGroup("Available sessions", availableSessions);
    }

    if (previousSessions.length > 0) {
      this.appendGroup("Previously available sessions", previousSessions);
    }

    if (this.moveSessionIdValue) {
      this.sessionSelectTarget.value = this.moveSessionIdValue;
    }
  }

  appendGroup(label, sessions) {
    const group = document.createElement("optgroup");
    group.label = label;

    sessions.forEach((session) => {
      const option = document.createElement("option");
      option.value = session.id;
      option.textContent = session.label;
      if (session.in_person !== undefined) option.dataset.inPerson = session.in_person;
      if (session.remote !== undefined) option.dataset.remote = session.remote;
      group.appendChild(option);
    })

    this.sessionSelectTarget.appendChild(group);
  }

  showNoSessions(message) {
    const option = document.createElement("option");
    option.value = "";
    option.textContent = message;
    option.disabled = true;
    this.sessionSelectTarget.appendChild(option);
  }
}
